import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Compass, Heart, Beer, Palette, Utensils, Sun, Moon, 
  Sparkles, ArrowRight, Clock, MapPin, ChevronRight,
  Calendar, Users, DollarSign, Wand2, Check, Building2, Star,
  Share2, Globe, User, Ticket, ShoppingBag, BookOpen
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from '@/contexts/LocationContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import EvisStar, { EvisLogo } from '@/components/EvisStar';
import api from '@/lib/api';
import { toast } from 'sonner';

// Icon mapping
const iconMap = {
  heart: Heart,
  beer: Beer,
  palette: Palette,
  utensils: Utensils,
  sun: Sun,
  moon: Moon,
  compass: Compass,
};

// Template Card Component
const TemplateCard = ({ template, onSelect, selected }) => {
  const Icon = iconMap[template.icon] || Compass;
  
  const gradients = {
    date_night: 'from-rose-500 to-pink-600',
    craft_beer: 'from-amber-500 to-orange-600',
    art_lovers: 'from-purple-500 to-indigo-600',
    foodie: 'from-emerald-500 to-teal-600',
    weekend_vibes: 'from-yellow-500 to-amber-600',
    nightlife: 'from-indigo-500 to-purple-600',
  };
  
  return (
    <motion.div
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(template)}
      className="cursor-pointer"
    >
      <Card className={`bg-slate-800 border-2 transition-all overflow-hidden ${
        selected?.id === template.id 
          ? 'border-indigo-500 ring-2 ring-indigo-500/20' 
          : 'border-slate-700 hover:border-slate-600'
      }`}>
        <div className={`h-24 sm:h-32 bg-gradient-to-br ${gradients[template.id] || 'from-indigo-500 to-purple-600'} flex items-center justify-center`}>
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>
        </div>
        <CardContent className="p-3 sm:p-4">
          <h3 className="font-bold text-slate-50 mb-1 text-sm sm:text-base">{template.title}</h3>
          <p className="text-xs sm:text-sm text-slate-400 line-clamp-2">{template.description}</p>
          {selected?.id === template.id && (
            <div className="mt-2 sm:mt-3 flex items-center gap-2 text-indigo-400">
              <Check className="w-4 h-4" />
              <span className="text-xs sm:text-sm font-medium">Selected</span>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Guide Card Component (for displaying generated guides)
const GuideCard = ({ guide, onClick, showPublish = false, onPublish }) => {
  const Icon = iconMap[guide.icon] || Compass;
  
  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="cursor-pointer"
    >
      <Card className="bg-slate-800 border-slate-700 hover:border-indigo-500/50 transition-all overflow-hidden h-full">
        <CardContent className="p-4 sm:p-6" onClick={() => onClick(guide)}>
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400" />
            </div>
            <div className="flex-grow min-w-0">
              <h3 className="font-bold text-slate-50 mb-1 text-sm sm:text-base">{guide.title}</h3>
              <p className="text-xs sm:text-sm text-slate-400 line-clamp-2 mb-2 sm:mb-3">{guide.description}</p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {guide.items?.length || 0} stops
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {guide.party_size || 2}
                </span>
                {guide.user_name && (
                  <span className="flex items-center gap-1 text-indigo-400">
                    <User className="w-3 h-3" />
                    {guide.user_name}
                  </span>
                )}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-600 hidden sm:block" />
          </div>
        </CardContent>
        {showPublish && !guide.is_public && (
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onPublish(guide.id);
              }}
              className="w-full border-indigo-500/50 text-indigo-400 hover:bg-indigo-500/10 text-xs sm:text-sm"
            >
              <Share2 className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
              Share to Community
            </Button>
          </div>
        )}
        {guide.is_public && (
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-0">
            <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs">
              <Globe className="w-3 h-3 mr-1" />
              Shared
            </Badge>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

// Guide Detail Modal
const GuideDetailModal = ({ guide, open, onClose, onConvert }) => {
  const [convertDate, setConvertDate] = useState('');
  const [converting, setConverting] = useState(false);
  const navigate = useNavigate();
  
  if (!guide) return null;
  
  const Icon = iconMap[guide.icon] || Compass;
  
  const handleConvert = async () => {
    if (!convertDate) {
      toast.error('Please select a date');
      return;
    }
    
    setConverting(true);
    try {
      const response = await api.post(`/guides/${guide.id}/to-itinerary?date=${convertDate}`);
      toast.success('Itinerary created!');
      navigate(`/itinerary/${response.data.itinerary_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create itinerary');
    } finally {
      setConverting(false);
    }
  };
  
  const handleReserve = (item) => {
    if (item.booking_url) {
      navigate(item.booking_url);
    } else if (item.item_type === 'event' && item.item_id) {
      navigate(`/events/${item.item_id}`);
    } else if (item.item_type === 'experience' && item.item_id) {
      navigate(`/experiences/${item.item_id}`);
    } else if (item.item_type === 'venue' && item.item_id) {
      navigate(`/venues/${item.item_id}`);
    } else {
      toast.info('Booking not available for this item');
    }
  };
  
  const getReserveButtonText = (itemType) => {
    switch (itemType) {
      case 'event': return 'Get Tickets';
      case 'experience': return 'Book Experience';
      case 'venue': return 'Reserve Table';
      default: return 'Reserve';
    }
  };
  
  const getReserveButtonIcon = (itemType) => {
    switch (itemType) {
      case 'event': return <Ticket className="w-4 h-4 mr-2" />;
      case 'experience': return <ShoppingBag className="w-4 h-4 mr-2" />;
      case 'venue': return <Calendar className="w-4 h-4 mr-2" />;
      default: return <ArrowRight className="w-4 h-4 mr-2" />;
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-slate-50 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-indigo-500/20 flex items-center justify-center">
              <Icon className="w-7 h-7 text-indigo-400" />
            </div>
            <div>
              <DialogTitle className="text-2xl headline-serif">{guide.title}</DialogTitle>
              <DialogDescription className="text-slate-400">
                {guide.description}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Guide Info */}
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm text-slate-400">
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              {guide.party_size || 2} people
            </span>
            <span className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              {guide.budget || 'moderate'} budget
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {guide.items?.length || 0} stops
            </span>
            {guide.duration_hours && (
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {guide.duration_hours} hours
              </span>
            )}
            {guide.planned_date && (
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(guide.planned_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
          
          {/* Stops */}
          <div className="space-y-4">
            <h4 className="font-bold text-slate-50">Your Itinerary</h4>
            {guide.items?.map((item, index) => (
              <div key={item.id || index} className="flex gap-4 p-4 bg-slate-800 rounded-xl">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                  {index < guide.items.length - 1 && (
                    <div className="w-0.5 h-full bg-slate-700 mt-2" />
                  )}
                </div>
                <div className="flex-grow">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-grow">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h5 className="font-bold text-slate-50">{item.name}</h5>
                        {item.item_type && (
                          <Badge className={`text-xs ${
                            item.item_type === 'event' ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' :
                            item.item_type === 'experience' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                            'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                          }`}>
                            {item.item_type}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mt-1">{item.description}</p>
                    </div>
                    {item.suggested_time && (
                      <Badge className="bg-slate-700 text-slate-300 whitespace-nowrap">
                        <Clock className="w-3 h-3 mr-1" />
                        {item.suggested_time}
                      </Badge>
                    )}
                  </div>
                  {item.ai_tip && (
                    <div className="mt-2 p-2 bg-gradient-to-r from-amber-500/10 to-indigo-500/10 rounded-lg border border-amber-500/20">
                      <p className="text-xs text-amber-300">
                        <Star className="w-3 h-3 inline mr-1 fill-current text-amber-400" />
                        <span className="font-medium">Evis's Tip:</span> {item.ai_tip}
                      </p>
                    </div>
                  )}
                  {(item.address || item.venue_name) && (
                    <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {item.address || item.venue_name}
                    </p>
                  )}
                  
                  {/* Reserve Button */}
                  {(item.bookable || item.item_id) && (
                    <div className="mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleReserve(item)}
                        className={`${
                          item.item_type === 'event' ? 'bg-indigo-600 hover:bg-indigo-700' :
                          item.item_type === 'experience' ? 'bg-purple-600 hover:bg-purple-700' :
                          'bg-emerald-600 hover:bg-emerald-700'
                        }`}
                      >
                        {getReserveButtonIcon(item.item_type)}
                        {getReserveButtonText(item.item_type)}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Convert to Itinerary */}
          <div className="p-4 bg-slate-800 rounded-xl space-y-3">
            <h4 className="font-bold text-slate-50">Plan Your Visit</h4>
            <p className="text-sm text-slate-400">Convert this guide to a collaborative itinerary to invite friends and keep track of your plans.</p>
            <div className="flex gap-4">
              <div className="flex-grow">
                <Label className="text-slate-400">Select Date</Label>
                <Input
                  type="date"
                  value={convertDate}
                  onChange={(e) => setConvertDate(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-slate-50"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleConvert}
                  disabled={converting || !convertDate}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  {converting ? 'Creating...' : 'Create Itinerary'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Guides Page
const Guides = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { currentCity, currentNeighborhood } = useLocation();
  const [templates, setTemplates] = useState([]);
  const [myGuides, setMyGuides] = useState([]);
  const [communityGuides, setCommunityGuides] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [featuredExperiences, setFeaturedExperiences] = useState([]);
  const [autoGuides, setAutoGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  const neighborhoodName = currentNeighborhood?.name || 'RiNo';
  const neighborhoodSlug = currentNeighborhood?.slug || currentNeighborhood?.id || 'rino';
  
  // Guide creation state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [preferences, setPreferences] = useState('');
  const [partySize, setPartySize] = useState('2');
  const [budget, setBudget] = useState('moderate');
  const [planName, setPlanName] = useState('');
  const [numStops, setNumStops] = useState('4');
  const [plannedDate, setPlannedDate] = useState('');
  const [startTime, setStartTime] = useState('6:00 PM');
  const [durationHours, setDurationHours] = useState('4');
  
  // Guide detail state
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [showGuideDetail, setShowGuideDetail] = useState(false);

  useEffect(() => {
    fetchData();
  }, [isAuthenticated, currentNeighborhood]);

  const fetchData = async () => {
    try {
      const [templatesRes, communityRes, eventsRes, experiencesRes] = await Promise.all([
        api.get('/guides/templates'),
        api.get(`/guides?limit=6${neighborhoodSlug ? `&neighborhood=${neighborhoodSlug}` : ''}`),
        api.get('/events?limit=4'),
        api.get('/vouchers?limit=4')
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setCommunityGuides(communityRes.data.guides || []);
      setFeaturedEvents(eventsRes.data || []);
      setFeaturedExperiences(experiencesRes.data || []);
      
      // Fetch auto-generated guides for the current city
      try {
        const autoRes = await api.get(`/guides/auto?limit=8${currentCity?.city ? `&city=${currentCity.city}` : ''}`);
        setAutoGuides(autoRes.data.guides || []);
      } catch { setAutoGuides([]); }
      
      if (isAuthenticated) {
        try {
          const myGuidesRes = await api.get('/guides/my-guides');
          setMyGuides(myGuidesRes.data.guides || []);
        } catch (e) {
          console.log('No personal guides');
        }
      }
    } catch (error) {
      console.error('Failed to fetch guides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    if (isAuthenticated) {
      setShowCreateDialog(true);
    } else {
      toast.error('Please sign in to create personalized guides');
      navigate('/auth?redirect=/guides');
    }
  };

  const handleGenerateGuide = async () => {
    if (!selectedTemplate) return;
    
    setGenerating(true);
    try {
      const response = await api.post('/guides/generate', {
        guide_type: selectedTemplate.id,
        preferences: preferences || null,
        party_size: parseInt(partySize),
        budget: budget,
        neighborhood: neighborhoodSlug,
        plan_name: planName || null,
        num_stops: parseInt(numStops),
        planned_date: plannedDate || null,
        start_time: startTime || '6:00 PM',
        duration_hours: parseInt(durationHours)
      });
      
      toast.success('Guide created!');
      setShowCreateDialog(false);
      setSelectedGuide(response.data);
      setShowGuideDetail(true);
      
      // Refresh my guides
      const myGuidesRes = await api.get('/guides/my-guides');
      setMyGuides(myGuidesRes.data.guides || []);
      
      // Reset form
      setSelectedTemplate(null);
      setPreferences('');
      setPartySize('2');
      setBudget('moderate');
      setPlanName('');
      setNumStops('4');
      setPlannedDate('');
      setStartTime('6:00 PM');
      setDurationHours('4');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate guide');
    } finally {
      setGenerating(false);
    }
  };

  const handleViewGuide = async (guide) => {
    try {
      const response = await api.get(`/guides/${guide.id}`);
      setSelectedGuide(response.data);
      setShowGuideDetail(true);
    } catch (error) {
      toast.error('Failed to load guide');
    }
  };

  const handlePublishGuide = async (guideId) => {
    try {
      await api.put(`/guides/${guideId}/publish`);
      toast.success('Guide shared to community!');
      
      // Refresh both lists
      const [myGuidesRes, communityRes] = await Promise.all([
        api.get('/guides/my-guides'),
        api.get('/guides?limit=6')
      ]);
      setMyGuides(myGuidesRes.data.guides || []);
      setCommunityGuides(communityRes.data.guides || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share guide');
    }
  };

  return (
    <ConsumerLayout darkTheme>
      
      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-indigo-900/50 to-[#0f172a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex justify-center mb-4 sm:mb-6">
              <EvisStar size="lg" className="sm:hidden" />
              <EvisStar size="xl" className="hidden sm:block" />
            </div>
            <h1 className="headline-serif text-3xl sm:text-4xl lg:text-6xl font-bold text-slate-50 mb-2">
              Meet <span className="bg-gradient-to-r from-amber-400 via-amber-300 to-indigo-400 bg-clip-text text-transparent">Evis</span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-slate-300 mb-3 sm:mb-4">
              Your AI Concierge for <span className="text-indigo-400">{neighborhoodName}</span>
            </p>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mb-6 sm:mb-8 px-4">
              I know every hidden gem in {neighborhoodName}. Tell me what kind of night you're looking for, and I'll create your perfect itinerary.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Template Selection */}
      <section className="py-10 sm:py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 sm:mb-10">
            <h2 className="headline-serif text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50 mb-2">
              What Kind of Night Are You Designing?
            </h2>
            <p className="text-sm sm:text-base text-slate-400">
              Choose a guide type and let <span className="text-amber-400 font-medium">Evis</span> create a personalized itinerary for you
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-40 sm:h-56 bg-slate-800 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onSelect={handleSelectTemplate}
                  selected={selectedTemplate}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* My Guides */}
      {isAuthenticated && myGuides.length > 0 && (
        <section className="py-10 sm:py-12 lg:py-16 bg-slate-900/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6 sm:mb-10">
              <div>
                <h2 className="headline-serif text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50 mb-1 sm:mb-2">
                  Your Guides
                </h2>
                <p className="text-sm sm:text-base text-slate-400">
                  Guides created by Evis for you
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {myGuides.map((guide) => (
                <GuideCard
                  key={guide.id}
                  guide={guide}
                  onClick={handleViewGuide}
                  showPublish={true}
                  onPublish={handlePublishGuide}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Community Guides */}
      {communityGuides.length > 0 && (
        <section className="py-10 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6 sm:mb-10">
              <div>
                <h2 className="headline-serif text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50 mb-1 sm:mb-2">
                  Community Guides
                </h2>
                <p className="text-sm sm:text-base text-slate-400">
                  Explore guides shared by other EVENT★GOERS
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {communityGuides.map((guide) => (
                <GuideCard
                  key={guide.id}
                  guide={guide}
                  onClick={handleViewGuide}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Auto-Generated Curated Guides */}
      {autoGuides.length > 0 && (
        <section className="py-10 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6 sm:mb-10">
              <div>
                <h2 className="headline-serif text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50 mb-1 sm:mb-2">
                  <BookOpen className="inline w-6 h-6 sm:w-8 sm:h-8 mr-2 text-indigo-400" />
                  Curated Guides
                </h2>
                <p className="text-sm sm:text-base text-slate-400">
                  Discover the best of {currentCity?.city || 'Denver'} — events, experiences, and venues
                </p>
              </div>
              <Link to={`/guides/auto${currentCity ? `/${currentCity.state?.toLowerCase()}/${currentCity.slug}` : ''}`}>
                <Button variant="ghost" className="text-indigo-400 hover:text-indigo-300">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {autoGuides.slice(0, 8).map((guide) => {
                const icons = { beer: '🍺', music: '🎵', moon: '🌙', sun: '☀️', star: '⭐', heart: '❤️', utensils: '🍽️', palette: '🎨', glass: '🍸', book: '📚', building: '🏢' };
                return (
                  <Link key={guide.id} to={`/guides/auto/${guide.location}/${guide.slug}`}>
                    <motion.div whileHover={{ y: -4 }} className="group bg-slate-800/50 rounded-xl p-5 border border-slate-700/50 hover:border-indigo-500/50 transition-all h-full">
                      <span className="text-2xl mb-3 block">{icons[guide.icon] || '📍'}</span>
                      <h3 className="font-bold text-slate-50 group-hover:text-indigo-400 transition-colors line-clamp-2">{guide.title}</h3>
                      <p className="text-sm text-slate-400 mt-2 line-clamp-2">{guide.description}</p>
                      <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                        <span>{guide.item_count} listing{guide.item_count !== 1 ? 's' : ''}</span>
                        {guide.neighborhood && <span>• {guide.neighborhood}</span>}
                      </div>
                    </motion.div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured Events Section */}
      {featuredEvents.length > 0 && (
        <section className="py-10 sm:py-12 lg:py-16 bg-gradient-to-b from-slate-900/50 to-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6 sm:mb-10">
              <div>
                <h2 className="headline-serif text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50 mb-1 sm:mb-2">
                  <Ticket className="inline w-6 h-6 sm:w-8 sm:h-8 mr-2 text-indigo-400" />
                  Upcoming Events
                </h2>
                <p className="text-sm sm:text-base text-slate-400">
                  Don't miss out on these experiences in {neighborhoodName}
                </p>
              </div>
              <Link to="/events">
                <Button variant="ghost" className="text-indigo-400 hover:text-indigo-300">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {featuredEvents.map((event) => (
                <Link key={event.id} to={`/events/${event.id}`}>
                  <motion.div
                    whileHover={{ y: -4 }}
                    className="group bg-slate-800/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-indigo-500/50 transition-all"
                  >
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <img 
                        src={event.images?.[0] || 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=400'} 
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {event.gold_exclusive && (
                        <Badge className="absolute top-2 right-2 bg-amber-500 text-amber-950">
                          <Star className="w-3 h-3 mr-1 fill-current" />Gold
                        </Badge>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-slate-50 line-clamp-1 group-hover:text-indigo-400 transition-colors">
                        {event.title}
                      </h3>
                      <p className="text-sm text-slate-400 mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {event.date} • {event.time}
                      </p>
                      <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {event.venue_name}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-indigo-400 font-semibold">${event.price}</span>
                        <span className="text-xs text-slate-500">
                          {event.capacity - (event.tickets_sold || 0)} left
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Experiences Section */}
      {featuredExperiences.length > 0 && (
        <section className="py-10 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6 sm:mb-10">
              <div>
                <h2 className="headline-serif text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50 mb-1 sm:mb-2">
                  <ShoppingBag className="inline w-6 h-6 sm:w-8 sm:h-8 mr-2 text-purple-400" />
                  Popular Experiences
                </h2>
                <p className="text-sm sm:text-base text-slate-400">
                  Book unique experiences and vouchers
                </p>
              </div>
              <Link to="/experiences">
                <Button variant="ghost" className="text-purple-400 hover:text-purple-300">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {featuredExperiences.map((exp) => (
                <Link key={exp.id} to={`/experiences/${exp.id}`}>
                  <motion.div
                    whileHover={{ y: -4 }}
                    className="group bg-slate-800/50 rounded-xl overflow-hidden border border-slate-700/50 hover:border-purple-500/50 transition-all"
                  >
                    <div className="aspect-[4/3] relative overflow-hidden">
                      <img 
                        src={exp.images?.[0] || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400'} 
                        alt={exp.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      {exp.gold_discount && (
                        <Badge className="absolute top-2 right-2 bg-amber-500 text-amber-950">
                          <Star className="w-3 h-3 mr-1 fill-current" />{exp.gold_discount}% off
                        </Badge>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-slate-50 line-clamp-1 group-hover:text-purple-400 transition-colors">
                        {exp.title}
                      </h3>
                      <p className="text-sm text-slate-400 mt-1 line-clamp-2">
                        {exp.description?.substring(0, 60)}...
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-purple-400 font-semibold">${exp.price}</span>
                        <span className="text-xs text-slate-500">
                          {exp.inventory_status?.remaining !== null 
                            ? `${exp.inventory_status.remaining} available`
                            : 'Available'}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Editorial Content - Curated Tips */}
      <section className="py-10 sm:py-12 lg:py-16 bg-gradient-to-b from-indigo-950/30 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="headline-serif text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50 mb-2">
              <Sparkles className="inline w-6 h-6 sm:w-8 sm:h-8 mr-2 text-amber-400" />
              Insider Tips from Evis
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
              Local knowledge to help you make the most of {neighborhoodName}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Tip Card 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-2xl p-6 border border-slate-700/50"
            >
              <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="font-bold text-lg text-slate-50 mb-2">Best Time to Visit</h3>
              <p className="text-slate-400 text-sm">
                {neighborhoodName}'s magic hour is between 7-9 PM on weekdays. Arrive early for happy hour deals, 
                then stay for the evening entertainment. Weekends get crowded after 8 PM.
              </p>
            </motion.div>

            {/* Tip Card 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-2xl p-6 border border-slate-700/50"
            >
              <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-indigo-400" />
              </div>
              <h3 className="font-bold text-lg text-slate-50 mb-2">Gold Club Perks</h3>
              <p className="text-slate-400 text-sm">
                Gold Club members save 15-25% on experiences and get exclusive access to member-only events. 
                The annual fee pays for itself after just 3-4 bookings!
              </p>
              <Link to="/gold-club" className="text-amber-400 text-sm font-medium hover:text-amber-300 mt-3 inline-block">
                Join Gold Club →
              </Link>
            </motion.div>

            {/* Tip Card 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-2xl p-6 border border-slate-700/50"
            >
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-bold text-lg text-slate-50 mb-2">Perfect Date Combo</h3>
              <p className="text-slate-400 text-sm">
                Start with a craft cocktail, catch some live music, then end at a cozy dessert spot. 
                Let me plan the perfect 4-hour date night for you!
              </p>
              <button 
                onClick={() => handleSelectTemplate(templates.find(t => t.id === 'date_night') || templates[0])}
                className="text-purple-400 text-sm font-medium hover:text-purple-300 mt-3 inline-block"
              >
                Plan a Date Night →
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Create Guide Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-50 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <EvisStar size="sm" />
              <DialogTitle className="headline-serif text-2xl">
                {selectedTemplate?.title}
              </DialogTitle>
            </div>
            <DialogDescription className="text-slate-400">
              Tell <span className="text-amber-400">Evis</span> more about what you're looking for
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Plan Name */}
            <div className="space-y-2">
              <Label className="text-slate-300">Name Your Plan (Optional)</Label>
              <Input
                placeholder="e.g., Sarah's Birthday Night Out"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-50 placeholder:text-slate-500"
              />
            </div>
            
            {/* Party Size & Number of Stops */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Party Size</Label>
                <Select value={partySize} onValueChange={setPartySize}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="1">Solo</SelectItem>
                    <SelectItem value="2">Couple (2)</SelectItem>
                    <SelectItem value="3">3 people</SelectItem>
                    <SelectItem value="4">4 people</SelectItem>
                    <SelectItem value="5">5 people</SelectItem>
                    <SelectItem value="6">6 people</SelectItem>
                    <SelectItem value="8">7-8 people</SelectItem>
                    <SelectItem value="10">9+ people</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Number of Stops</Label>
                <Select value={numStops} onValueChange={setNumStops}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="2">2 stops</SelectItem>
                    <SelectItem value="3">3 stops</SelectItem>
                    <SelectItem value="4">4 stops</SelectItem>
                    <SelectItem value="5">5 stops</SelectItem>
                    <SelectItem value="6">6 stops</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Date & Start Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Date (Optional)</Label>
                <Input
                  type="date"
                  value={plannedDate}
                  onChange={(e) => setPlannedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-slate-800 border-slate-700 text-slate-50"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Start Time</Label>
                <Select value={startTime} onValueChange={setStartTime}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="12:00 PM">12:00 PM</SelectItem>
                    <SelectItem value="1:00 PM">1:00 PM</SelectItem>
                    <SelectItem value="2:00 PM">2:00 PM</SelectItem>
                    <SelectItem value="3:00 PM">3:00 PM</SelectItem>
                    <SelectItem value="4:00 PM">4:00 PM</SelectItem>
                    <SelectItem value="5:00 PM">5:00 PM</SelectItem>
                    <SelectItem value="6:00 PM">6:00 PM</SelectItem>
                    <SelectItem value="7:00 PM">7:00 PM</SelectItem>
                    <SelectItem value="8:00 PM">8:00 PM</SelectItem>
                    <SelectItem value="9:00 PM">9:00 PM</SelectItem>
                    <SelectItem value="10:00 PM">10:00 PM</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Duration & Budget */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Duration</Label>
                <Select value={durationHours} onValueChange={setDurationHours}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="2">2 hours</SelectItem>
                    <SelectItem value="3">3 hours</SelectItem>
                    <SelectItem value="4">4 hours</SelectItem>
                    <SelectItem value="5">5 hours</SelectItem>
                    <SelectItem value="6">6 hours</SelectItem>
                    <SelectItem value="8">8 hours (full day)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-slate-300">Budget Level</Label>
                <Select value={budget} onValueChange={setBudget}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="budget">Budget-Friendly</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Preferences */}
            <div className="space-y-2">
              <Label className="text-slate-300">Any Specific Preferences? (Optional)</Label>
              <Textarea
                placeholder="e.g., We love live music, prefer outdoor seating, looking for Instagram-worthy spots..."
                value={preferences}
                onChange={(e) => setPreferences(e.target.value)}
                className="bg-slate-800 border-slate-700 text-slate-50 placeholder:text-slate-500 min-h-[80px]"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerateGuide}
              disabled={generating}
              className="bg-gradient-to-r from-amber-500 to-indigo-600 hover:from-amber-600 hover:to-indigo-700"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Evis is thinking...
                </>
              ) : (
                <>
                  <Star className="w-4 h-4 mr-2 fill-current" />
                  Ask Evis
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Guide Detail Modal */}
      <GuideDetailModal
        guide={selectedGuide}
        open={showGuideDetail}
        onClose={() => setShowGuideDetail(false)}
      />

    </ConsumerLayout>
  );
};

export default Guides;
